import {useEffect, useMemo, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import pagesCss from './pages.module.css';
import css from '../components/note/note.module.css';
import commonCss from '../components/common/common.module.css';
import type {Note} from '../types/Note';
import {
  subscribeNote,
  updateNoteFields,
  deleteNoteDoc,
} from '../services/firestoreNotes';
import {
  translateNote,
  summarizeNote,
  shareNote,
  unshareNote,
} from '../services/functionsService';
import {downloadNoteBlob, extensionOf} from '../services/storageService';
import {useAuth} from '../context/AuthProvider';
import {useToast} from '../context/ToastProvider';
import {useConfirm} from '../context/ConfirmProvider';
import {useUpgrade} from '../context/UpgradeProvider';
import {Icon, type IconName} from '../components/common/Icon';
import {
  Button,
  IconButton,
  Markdown,
  Menu,
  MenuItem,
  Spinner,
} from '../components/common/ui';
import {AudioPlayer} from '../components/note/AudioPlayer';
import {NoteProgress} from '../components/note/NoteProgress';
import {ChatPanel} from '../components/note/ChatPanel';
import {QuizTab} from '../components/note/QuizTab';
import {FlashcardsTab} from '../components/note/FlashcardsTab';
import {LANGUAGES, flagEmoji, languageName} from '../services/languages';
import {formatDate, formatDuration, downloadBlob} from '../utils';
import {getSummaryLanguage} from '../services/settings';
import posthog, {isPostHogConfigured} from '../posthog';

type TabKey = 'summary' | 'transcript' | 'chat' | 'quiz' | 'flashcards';

const TABS: Array<{key: TabKey; icon: IconName; labelKey: string}> = [
  {key: 'summary', icon: 'sparkles', labelKey: 'Summary'},
  {key: 'transcript', icon: 'notes', labelKey: 'Transcription'},
  {key: 'chat', icon: 'chat', labelKey: 'AskAI'},
  {key: 'quiz', icon: 'quiz', labelKey: 'Quiz'},
  {key: 'flashcards', icon: 'cards', labelKey: 'Flashcards'},
];

/** Markdown body with copy + inline edit (mobile's EditModal equivalent). */
function EditableMarkdown({
  value,
  onSave,
  copiedToastKey,
}: {
  value: string;
  onSave: (next: string) => Promise<void>;
  copiedToastKey: string;
}) {
  const {t} = useTranslation();
  const showToast = useToast();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);

  const copy = async () => {
    await navigator.clipboard.writeText(value);
    showToast(t(copiedToastKey), 'success');
  };

  if (editing) {
    return (
      <div className={css.tabContent}>
        <textarea
          className={commonCss.textarea}
          style={{minHeight: 320}}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          autoFocus
        />
        <div className={css.tabToolbar} style={{marginTop: 12}}>
          <Button
            variant="primary"
            icon="check"
            loading={saving}
            onClick={async () => {
              setSaving(true);
              try {
                await onSave(draft);
                setEditing(false);
                showToast(t('Saved'), 'success');
              } finally {
                setSaving(false);
              }
            }}
          >
            {t('Save')}
          </Button>
          <Button variant="ghost" onClick={() => setEditing(false)}>
            {t('Cancel')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={css.tabContent}>
      <div className={`${css.tabToolbar} no-print`}>
        <Button variant="ghost" icon="copy" onClick={copy}>
          {t('Copy')}
        </Button>
        <Button
          variant="ghost"
          icon="edit"
          onClick={() => {
            setDraft(value);
            setEditing(true);
          }}
        >
          {t('Edit_Content')}
        </Button>
      </div>
      <Markdown>{value}</Markdown>
    </div>
  );
}

export function NoteDetailPage() {
  const {noteId} = useParams<{noteId: string}>();
  const {t, i18n} = useTranslation();
  const {uid} = useAuth();
  const navigate = useNavigate();
  const showToast = useToast();
  const confirm = useConfirm();
  const {handleLimitError} = useUpgrade();

  const [note, setNote] = useState<Note | null | undefined>(undefined);
  const [tab, setTab] = useState<TabKey>('summary');
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [busy, setBusy] = useState<'translate' | 'summarize' | 'audio' | 'share' | null>(
    null,
  );

  useEffect(() => {
    if (!uid || !noteId) {
      return;
    }
    return subscribeNote(uid, noteId, setNote);
  }, [uid, noteId]);

  const translateLanguages = useMemo(
    () => LANGUAGES.filter(lang => lang.code !== null),
    [],
  );

  if (note === undefined) {
    return (
      <div className={commonCss.emptyState}>
        <Spinner large />
      </div>
    );
  }
  if (note === null || !uid || !noteId) {
    return (
      <div className={commonCss.emptyState}>
        <p>{t('Note_not_found')}</p>
        <Button variant="secondary" onClick={() => navigate('/notes')}>
          {t('GoBack')}
        </Button>
      </div>
    );
  }

  if (note.status !== 'ready') {
    return (
      <div className={pagesCss.page}>
        <NoteProgress note={note} />
      </div>
    );
  }

  const saveTitle = async () => {
    setEditingTitle(false);
    const next = titleDraft.trim();
    if (next && next !== note.title) {
      await updateNoteFields(uid, noteId, {title: next});
    }
  };

  const doTranslate = async (code: string) => {
    setBusy('translate');
    try {
      await translateNote(noteId, code);
      if (isPostHogConfigured) {
        posthog.capture('note_translated', {language: code});
      }
      showToast(t('Done'), 'success');
    } catch (e) {
      if (!handleLimitError(e)) {
        console.warn(e);
        showToast(t('ErrorFetch'), 'error');
      }
    } finally {
      setBusy(null);
    }
  };

  const doSummarize = async () => {
    setBusy('summarize');
    try {
      // 'auto' is an explicit override (null would mean "no override" and
      // fall back to the note's snapshotted language on the backend).
      await summarizeNote(noteId, getSummaryLanguage() ?? 'auto');
      if (isPostHogConfigured) {
        posthog.capture('note_summarized');
      }
      showToast(t('summarizeSaved'), 'success');
    } catch (e) {
      if (!handleLimitError(e)) {
        console.warn(e);
        showToast(t('ErrorFetch'), 'error');
      }
    } finally {
      setBusy(null);
    }
  };

  const downloadAudio = async () => {
    if (!note.storagePath) {
      return;
    }
    setBusy('audio');
    try {
      const blob = await downloadNoteBlob(note.storagePath);
      const ext = extensionOf(note.storagePath);
      const name = `${(note.title ?? 'owlly-note').slice(0, 60)}.${ext}`;
      downloadBlob(blob, name);
    } catch (e) {
      console.warn(e);
      showToast(t('Error_share'), 'error');
    } finally {
      setBusy(null);
    }
  };

  const removeNote = async () => {
    const ok = await confirm({
      title: t('Delete_Note'),
      message: t('Confirm_delete_note'),
      confirmText: t('Delete'),
      danger: true,
    });
    if (ok) {
      await deleteNoteDoc(uid, noteId);
      if (isPostHogConfigured) {
        posthog.capture('note_deleted');
      }
      navigate('/notes');
    }
  };

  const doShareLink = async () => {
    setBusy('share');
    try {
      const {url} = await shareNote(noteId);
      await navigator.clipboard.writeText(url);
      if (isPostHogConfigured) {
        posthog.capture('note_shared');
      }
      showToast(t('WebLinkCopied'), 'success');
    } catch (e) {
      console.warn(e);
      showToast(t('Error_share'), 'error');
    } finally {
      setBusy(null);
    }
  };

  const doStopSharing = async () => {
    setBusy('share');
    try {
      await unshareNote(noteId);
      if (isPostHogConfigured) {
        posthog.capture('note_sharing_stopped');
      }
      showToast(t('WebStopSharingDone'), 'success');
    } catch (e) {
      console.warn(e);
      showToast(t('Error_share'), 'error');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className={`${pagesCss.page} print-area`}>
      <header className={css.detailHeader}>
        <span className={css.detailEmoji}>{note.emoji ?? '📝'}</span>
        <div className={css.detailTitleWrap}>
          {editingTitle ? (
            <input
              className={css.detailTitleInput}
              value={titleDraft}
              onChange={e => setTitleDraft(e.target.value)}
              onBlur={saveTitle}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  void saveTitle();
                }
                if (e.key === 'Escape') {
                  setEditingTitle(false);
                }
              }}
              autoFocus
              maxLength={140}
            />
          ) : (
            <h1
              className={css.detailTitle}
              onClick={() => {
                setTitleDraft(note.title ?? '');
                setEditingTitle(true);
              }}
              title={t('EditTitle')}
            >
              {note.title ?? t('Note_title')}
            </h1>
          )}
          <div className={css.detailMeta}>
            <span>{formatDate(note.createdAt, i18n.language)}</span>
            {note.fileType === 'audio' && note.duration > 0 && (
              <span>{formatDuration(note.duration)}</span>
            )}
            {note.language && <span>{languageName(note.language)}</span>}
            {note.fileType === 'youtube' && note.videoId && (
              <a
                href={`https://www.youtube.com/watch?v=${note.videoId}`}
                target="_blank"
                rel="noreferrer"
              >
                YouTube <Icon name="externalLink" size={11} />
              </a>
            )}
          </div>
        </div>
        <div className={`${css.detailActions} no-print`}>
          <Menu
            trigger={
              <IconButton
                icon={busy === 'translate' ? 'refresh' : 'translate'}
                title={t('Translate')}
              />
            }
          >
            {close => (
              <>
                {translateLanguages.map(lang => (
                  <MenuItem
                    key={`${lang.code}-${lang.flag}`}
                    onClick={() => {
                      close();
                      void doTranslate(lang.code!);
                    }}
                  >
                    <span>{flagEmoji(lang.flag)}</span>
                    {lang.language}
                  </MenuItem>
                ))}
              </>
            )}
          </Menu>
          <Menu trigger={<IconButton icon="download" title={t('Share')} />}>
            {close => (
              <>
                <MenuItem
                  icon="link"
                  onClick={() => {
                    close();
                    void doShareLink();
                  }}
                >
                  {note.shared ? t('WebCopyLink') : t('WebShareNote')}
                </MenuItem>
                {note.shared && (
                  <MenuItem
                    icon="x"
                    onClick={() => {
                      close();
                      void doStopSharing();
                    }}
                  >
                    {t('WebStopSharing')}
                  </MenuItem>
                )}
                <MenuItem
                  icon="printer"
                  onClick={() => {
                    close();
                    window.print();
                  }}
                >
                  {t('WebExportPDF')}
                </MenuItem>
                {note.fileType === 'audio' && note.storagePath && (
                  <MenuItem
                    icon="download"
                    onClick={() => {
                      close();
                      void downloadAudio();
                    }}
                  >
                    {t('WebDownloadAudio')}
                  </MenuItem>
                )}
                <MenuItem
                  icon="refresh"
                  onClick={() => {
                    close();
                    void doSummarize();
                  }}
                >
                  {t('Get Summary')}
                </MenuItem>
              </>
            )}
          </Menu>
          <IconButton
            icon="trash"
            title={t('Delete_Note')}
            style={{color: 'var(--color-danger)'}}
            onClick={() => void removeNote()}
          />
        </div>
      </header>

      {note.fileType === 'audio' && (note.audioUrl || note.storagePath) && (
        <AudioPlayer note={note} />
      )}

      {(busy === 'translate' || busy === 'summarize') && (
        <div
          className={css.detailMeta}
          style={{padding: '10px 32px 0', gap: 8}}
        >
          <Spinner /> {busy === 'translate' ? t('Translate') : t('Summarize')}…
        </div>
      )}

      <nav className={`${css.tabs} no-print`}>
        {TABS.map(item => (
          <button
            key={item.key}
            className={tab === item.key ? css.tabActive : css.tab}
            onClick={() => setTab(item.key)}
            type="button"
          >
            <span className={css.tabInner}>
              <Icon name={item.icon} size={15} />
              {t(item.labelKey)}
            </span>
            <span
              className={
                tab === item.key ? css.tabIndicator : css.tabIndicatorGhost
              }
            />
          </button>
        ))}
      </nav>

      {tab === 'chat' ? (
        <ChatPanel
          noteId={noteId}
          emptyState={
            <div className={pagesCss.aiHero}>
              <h2 className={pagesCss.aiHeroTitle}>{t('AskOwllyAnything')}</h2>
              <p className={pagesCss.aiHeroSub}>{t('AskAnythingHint')}</p>
            </div>
          }
        />
      ) : (
        <div className={css.tabBody}>
          {tab === 'summary' &&
            (note.summarize ? (
              <EditableMarkdown
                value={note.summarize}
                copiedToastKey="SummaryCopied"
                onSave={next => updateNoteFields(uid, noteId, {summarize: next})}
              />
            ) : (
              <div className={commonCss.emptyState}>
                <Button
                  variant="primary"
                  icon="sparkles"
                  loading={busy === 'summarize'}
                  onClick={doSummarize}
                >
                  {t('Get Summary')}
                </Button>
              </div>
            ))}
          {tab === 'transcript' &&
            (note.content ? (
              <EditableMarkdown
                value={note.content}
                copiedToastKey="TranscriptCopied"
                onSave={next => updateNoteFields(uid, noteId, {content: next})}
              />
            ) : (
              <div className={commonCss.emptyState}>
                <p>{t('ErrorFetch')}</p>
              </div>
            ))}
          {tab === 'quiz' && <QuizTab noteId={noteId} />}
          {tab === 'flashcards' && <FlashcardsTab noteId={noteId} />}
        </div>
      )}
    </div>
  );
}
